// ============================================
// CHILD COMPONENT - InputEntityCalendarComponent
// ============================================

import { ChangeDetectionStrategy, Component, EventEmitter, inject, Inject, Input, OnDestroy, Optional, Output, signal, ViewChild, AfterViewInit, ChangeDetectorRef, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { InputCalendarComponent, InputCalendarModel } from '../input-calendar/input-calendar.component';
import { CommonModule } from '@angular/common';
import { MatCalendar, MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DATE_FORMATS, DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { Subject, startWith, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { CustomDateAdapter, MY_DATE_FORMATS } from '@app/_configs/mat-date.config';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

@Component({
    selector: 'app-input-entity-calendar',
    imports: [
        CommonModule,
        MatDatepickerModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule
    ],
    providers: [
        { provide: DateAdapter, useClass: CustomDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }
    ],
    templateUrl: './input-entity-calendar.component.html',
    styleUrl: './input-entity-calendar.component.scss'
})
export class InputEntityCalendarComponent implements OnDestroy, AfterViewInit, OnInit {
  @ViewChild(MatCalendar) calendar!: MatCalendar<Date>;
  @ViewChild('picker') picker!: MatDatepicker<Date>;
  @Input() selectedDate: Date | null = null;
  @Input() minDate: Date | string | null = '2000-01-01';
  @Input() maxDate: Date | string | null = '2100-01-01';
  @Output() onDateSelected = new EventEmitter<Date>();
  @Output() onYearSelected = new EventEmitter<Date>();
  @Output() onMonthSelected = new EventEmitter<Date>();
  @Output() onViewChanged = new EventEmitter<'month' | 'year'>();

  readonly entityCalendarHeader = EntityCalendarHeader;
  destroy$ = new Subject<void>();
  isMonthView = true;
  private previousView: string = 'month';
  private cdr = inject(ChangeDetectorRef);

  constructor(
    @Optional() public dialogRef: MatDialogRef<InputCalendarComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: InputCalendarModel,
  ) {
  }

  ngOnInit() {
    if (this.data) {
      this.selectedDate = this.data.selectedDate || new Date();
      this.minDate = this.data.minDate || null;
      this.maxDate = this.data.maxDate || null;
    } else {
      this.selectedDate = this.selectedDate || new Date();
    }

    EntityCalendarHeader.viewChanged
      .pipe(takeUntil(this.destroy$))
      .subscribe(view => {
        this.isMonthView = view === 'month';
        this.onViewChanged.emit(view === 'month' ? 'month' : 'year');
      });
  }

  ngAfterViewInit() {
    if (this.calendar) {
      this.monitorCalendarState();
    }
  }

  private monitorCalendarState() {
    const calendar = this.calendar as any;

    const viewChangeSubject = new Subject<string>();
    let shouldPreventClose = false;

    viewChangeSubject.pipe(
      debounceTime(50),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(currentView => {
      console.log('[Child] View changed from', this.previousView, 'to', currentView);

      if (this.previousView === 'year' && currentView === 'month') {
        console.log('[Child] MONTH SELECTED');
        shouldPreventClose = true;
        this.onMonthSelected.emit(calendar.activeDate);
        EntityCalendarHeader.monthSelected.next(calendar.activeDate);

        // Reset flag after emission
        setTimeout(() => {
          shouldPreventClose = false;
        }, 100);
      } else if (this.previousView === 'multi-year' && currentView === 'year') {
        console.log('[Child] YEAR SELECTED');
        shouldPreventClose = true;
        this.onYearSelected.emit(calendar.activeDate);
        EntityCalendarHeader.yearSelected.next(calendar.activeDate);

        // Reset flag after emission
        setTimeout(() => {
          shouldPreventClose = false;
        }, 100);
      }

      this.previousView = currentView;
    });

    // Intercept _dateSelected to prevent selectedChange event on year/month selection
    const originalDateSelected = calendar._dateSelected.bind(calendar);
    calendar._dateSelected = (date: any, event?: Event) => {
      const currentView = calendar.currentView;

      // Store BEFORE the selection
      const previousView = currentView;

      originalDateSelected(date);

      // Check AFTER Material processes it
      setTimeout(() => {
        const newView = calendar.currentView;
        console.log(`View transition: ${previousView} → ${newView}`);
        viewChangeSubject.next(newView);
      }, 0);
    };

    // Intercept the selectedChange EventEmitter to filter events
    const originalEmit = calendar.selectedChange.emit.bind(calendar.selectedChange);
    calendar.selectedChange.emit = (selectedDate: Date) => {
      const currentView = calendar.currentView;

      console.log('[Child] selectedChange.emit called, currentView:', currentView);

      // Only emit if in month view (day selection)
      if (currentView === 'month') {
        console.log('[Child] Emitting selectedChange for DAY selection');
        originalEmit(selectedDate);
      } else {
        console.log('[Child] Blocking selectedChange event for', currentView, 'selection');
        // Don't emit the event for year/month selection
      }
    };

    // Monitor state changes
    calendar.stateChanges.pipe(
      debounceTime(50),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      const currentView = calendar.currentView;
      console.log('[Child] State changed, currentView:', currentView);
      viewChangeSubject.next(currentView);
    });
  }



  openCalendar() {
    this.picker.open();
  }

  closeCalendar() {
    this.picker.close();
  }

  // Also update dateSelected method to handle the filtered events
  dateSelected(event: any) {
    console.log('[Child] dateSelected, isMonthView:', this.isMonthView);

    // CRITICAL: Check if we're in month view
    if (!this.isMonthView) {
      console.log('[Child] Blocking close - not in month view');
      return; // ❌ Don't proceed
    }

    this.selectedDate = event;
    console.log('[Child] Closing calendar');

    if (this.dialogRef) {
      this.dialogRef.close(event);
    } else {
      this.onDateSelected.emit(event);
    }

  }

  // Alternative: Add a method to manually control event propagation in the template
  preventEventPropagation(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

// ============================================
// HEADER COMPONENT (same as before)
// ============================================

@Component({
    selector: 'entity-calendar-header',
    styles: `
    .example-header {
      display: flex;
      align-items: center;
      padding: 16px;
    }
    .left-arrow, .right-arrow {
      color: var(--color8);
    }
    :host-context([dir="rtl"]) .left-arrow mat-icon,
    :host-context([dir="rtl"]) .right-arrow mat-icon {
      transform: scaleX(-1);
    }
    .example-header-label {
      flex: 1;
      font-weight: 500;
      text-align: center;
      cursor: pointer;
      border-radius: 4px;
      transition: background-color 0.2s ease;
      color: var(--color8);
      font-size: 18px;
      user-select: none;
    }
    // .example-header-label:hover {
    //   background-color: rgba(0, 0, 0, 0.04);
    // }
    // .example-header-label.year-mode {
    //   background-color: rgba(63, 81, 181, 0.1);
    // }
  `,
    template: `
    <div class="example-header">
      <button matIconButton (click)="previousClicked(this.currentView(),$event);" class="left-arrow">
        <mat-icon>keyboard_arrow_left</mat-icon>
      </button>
      <span 
        class="example-header-label"
        (click)="toggleView($event)">
        {{periodLabel()}}
      </span>
      <button matIconButton (click)="nextClicked(this.currentView(),$event);" class="right-arrow">
        <mat-icon>keyboard_arrow_right</mat-icon>
      </button>
    </div>
  `,
    imports: [MatButtonModule, MatIconModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EntityCalendarHeader<D> implements OnDestroy {
  private _calendar = inject<MatCalendar<D>>(MatCalendar);
  private _dateAdapter = inject<DateAdapter<D>>(DateAdapter);
  private _dateFormats = inject(MAT_DATE_FORMATS);
  private _destroyed = new Subject<void>();

  static arrowClick = new Subject<Date>();
  static updatePeriodLabel = new Subject<string>();
  static viewChanged = new Subject<string>();
  static yearSelected = new Subject<Date>();
  static monthSelected = new Subject<Date>();

  readonly periodLabel = signal('');
  readonly currentView = signal<'month' | 'year' | 'multi-year'>('month');

  constructor() {
    // Listen to calendar state changes
    this._calendar.stateChanges
      .pipe(startWith(null), takeUntil(this._destroyed))
      .subscribe(() => {
        this.updatePeriodLabel();
      });

    // Listen to calendar view changes
    this._calendar.viewChanged
      .pipe(takeUntil(this._destroyed))
      .subscribe((view: any) => {
        this.currentView.set(view);
        EntityCalendarHeader.viewChanged.next(view);
        this.updatePeriodLabel();
      });

    // Listen to month selection
    this._calendar.monthSelected
      .pipe(takeUntil(this._destroyed))
      .subscribe((date: D) => {
        EntityCalendarHeader.monthSelected.next(date as any);
        this.updatePeriodLabel();
      });

    // Listen to year selection
    this._calendar.yearSelected
      .pipe(takeUntil(this._destroyed))
      .subscribe((date: D) => {
        this.updatePeriodLabel();
      });
  }

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }

  updatePeriodLabel() {
    const view = this.currentView();

    if (view === 'month') {
      const formatted = this._dateAdapter.format(
        this._calendar.activeDate,
        this._dateFormats.display.monthYearLabel
      );
      const active: any = this._calendar.activeDate;
      EntityCalendarHeader.yearSelected.next(active.toDate?.() || active);
      this.periodLabel.set(formatted.charAt(0).toUpperCase() + formatted.slice(1).toLowerCase());

      const monthView = document.querySelector('mat-month-view');
      if (monthView) {
        monthView.classList.add('calendar-month-view');
      }


    } else if (view === 'year') {
      const year = this._dateAdapter.getYear(this._calendar.activeDate);
      const calendarBody: any = document.querySelector('.mat-calendar-body');
      const allLabels = calendarBody.querySelectorAll('.mat-calendar-body-label');
      if (allLabels.length > 0) {
        allLabels.forEach((label: any) => label.remove());
      }
      // Remove injected month-view placeholder cells when entering year view
      const placeholders = calendarBody.querySelectorAll('.mat-calendar-body-disabled');
      placeholders.forEach((el: any) => el.remove());
      this.periodLabel.set(year.toString());
    } else if (view === 'multi-year') {
      const calendarBody: any = document.querySelector('.mat-calendar-body');
      if (calendarBody) {
        // Remove injected month-view placeholder cells when entering multi-year view
        const placeholders = calendarBody.querySelectorAll('.mat-calendar-body-disabled');
        placeholders.forEach((el: any) => el.remove());
        const allLabels = calendarBody.querySelectorAll('.mat-calendar-body-label');
        allLabels.forEach((label: any) => label.remove());
      }
    }
    EntityCalendarHeader.updatePeriodLabel.next(this.periodLabel());
  }

  toggleView(event: any) {
    event.stopPropagation();
    const currentView = this.currentView();

    if (currentView === 'month') {
      // Switch to year/multi-year view
      this._calendar.currentView = 'multi-year';
      this.currentView.set('multi-year');
    } else {
      // Switch back to month view
      this._calendar.currentView = 'month';
      this.currentView.set('month');
    }

    this.updatePeriodLabel();
    EntityCalendarHeader.viewChanged.next(this.currentView());
  }

  previousClicked(mode: 'month' | 'year' | 'multi-year', event: any) {
    event.stopPropagation();
    const view = this.currentView();

    if (mode === 'multi-year') {
      const yearsPerPage = 24; // default Angular Material value is 24
      this._calendar.activeDate = this._dateAdapter.addCalendarYears(
        this._calendar.activeDate,
        -yearsPerPage
      );
    } else if (mode === 'year') {
      this._calendar.activeDate = this._dateAdapter.addCalendarYears(
        this._calendar.activeDate,
        -1
      );
    } else if (mode === 'month') {
      this._calendar.activeDate = this._dateAdapter.addCalendarMonths(
        this._calendar.activeDate,
        -1
      );
      const active: any = this._calendar.activeDate;
      EntityCalendarHeader.arrowClick.next(active.toDate?.() || active);
    }


  }

  nextClicked(mode: 'month' | 'year' | 'multi-year', event: any) {
    event.stopPropagation();
    const view = this.currentView();

    if (mode === 'multi-year') {
      const yearsPerPage = 24;
      this._calendar.activeDate = this._dateAdapter.addCalendarYears(
        this._calendar.activeDate,
        yearsPerPage
      );
    } else if (mode === 'year') {
      this._calendar.activeDate = this._dateAdapter.addCalendarYears(
        this._calendar.activeDate,
        1
      );
    } else if (mode === 'month') {
      this._calendar.activeDate = this._dateAdapter.addCalendarMonths(
        this._calendar.activeDate,
        1
      );
      const active: any = this._calendar.activeDate;
      EntityCalendarHeader.arrowClick.next(active.toDate?.() || active);
    }


  }
}